import { Product, ProductTenor, ProductType } from '@protos/product';
import { OrderBookTopMessage } from '@protos/v2/orderBookTop';
import { Ticker } from '@protos/v2/ticker';
import { streamV2OrderBookTop, streamV2Product } from '@services/context';
import { useEffect, useRef, useState } from 'react';
import { getRFQSymbolForMonthly, getRFQSymbolForSingleTenor } from '../../trading/utils';

export const useSinglePriceTicker = (
  product: Product | null,
  tenor: ProductTenor | null,
  frontTenor: ProductTenor | null,
  backTenor: ProductTenor | null
) => {
  const [mid, setMid] = useState<number | null>(null);
  const [bid, setBid] = useState<number | null>(null);
  const [ask, setAsk] = useState<number | null>(null);

  const lastProductSubscription = useRef<string | null>(null);
  const lastProductSymbol = useRef<string | null>(null);

  const getTickerCallback = () => {
    return (ticker: Ticker) => {
      if (ticker.symbol === lastProductSubscription.current) {
        setMid(Number(ticker.mid));
      }
    };
  };

  const getOrderBookCallback = () => {
    return (obtm: OrderBookTopMessage) => {
      if (obtm.symbol === lastProductSubscription.current) {
        setBid(Number(obtm.buy.price));
        setAsk(Number(obtm.sell.price));
      }
    };
  };

  const tickerCallbackRef = useRef(getTickerCallback());
  const orderBookTopCallbackRef = useRef(getOrderBookCallback());

  const reset = () => {
    lastProductSubscription.current = null;
    lastProductSymbol.current = null;
    setBid(null);
    setMid(null);
    setAsk(null);
  };

  useEffect(() => {
    // Unsubscribe the old callbacks
    if (lastProductSymbol.current !== null) {
      streamV2Product.unsubscribe([lastProductSymbol.current], tickerCallbackRef.current);
      streamV2OrderBookTop.unsubscribe([lastProductSymbol.current], orderBookTopCallbackRef.current);
    }

    // Don't bother resubscribing if there's not enough information
    if (product === null || tenor === null) {
      reset();
      return;
    }

    // Set new values to look at
    const newProductSymbol = product.symbol;

    // Get an RFQ symbol
    let newSubscription: string = '';

    if (product.product_type === ProductType.CALENDAR) {
      newSubscription = getRFQSymbolForMonthly(product, tenor);
    } else if (tenor) {
      newSubscription = getRFQSymbolForSingleTenor(product, tenor);
    }

    // Create new callbacks
    tickerCallbackRef.current = getTickerCallback();
    orderBookTopCallbackRef.current = getOrderBookCallback();

    // Subscribe to the streams
    streamV2Product.subscribe([newProductSymbol], tickerCallbackRef.current);
    streamV2OrderBookTop.subscribe([newProductSymbol], orderBookTopCallbackRef.current);

    // Update the refs
    lastProductSubscription.current = newSubscription;
    lastProductSymbol.current = newProductSymbol;

    setMid(null);
    setBid(null);
    setAsk(null);

    return () => {
      if (lastProductSymbol.current) {
        streamV2Product.unsubscribe([lastProductSymbol.current], tickerCallbackRef.current);
        streamV2OrderBookTop.unsubscribe([lastProductSymbol.current], orderBookTopCallbackRef.current);
      }
    };
  }, [product, tenor, frontTenor, backTenor]);

  return { bid, mid, ask };
};
